import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProgressIndicator from '../../../components/ui/ProgressIndicator';
import { lumoEdgeFunctionService } from '../../../services/lumoEdgeFunctionService';
import { useAuth } from '../../../contexts/AuthContext';

const PracticeInterface = ({ objection, onClose, onSubmit }) => {
  const { user } = useAuth();
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!response?.trim()) {
      setError('Please enter a response before submitting');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      // Call Lumo Edge Function to evaluate objection response
      const evaluationResult = await lumoEdgeFunctionService?.evaluateObjectionWithLumo({
        userId: user?.id,
        objectionId: objection?.id,
        userResponseText: response
      });

      if (!evaluationResult?.success) {
        throw new Error(evaluationResult?.error?.message || 'Failed to evaluate response');
      }

      // Format feedback for UI
      const formattedFeedback = {
        score: evaluationResult?.data?.lumoScore || 0,
        feedbackText: evaluationResult?.data?.feedbackText || 'No feedback available',
        suggestedScript: evaluationResult?.data?.suggestedScript || '',
        attemptId: evaluationResult?.data?.attemptId
      };

      setFeedback(formattedFeedback);

      // Call parent onSubmit callback
      onSubmit(response, formattedFeedback);
    } catch (err) {
      console.error('Error evaluating objection response:', err);
      setError(err?.message || 'Failed to evaluate response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="fixed inset-0 z-[200] bg-background/95 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Practice Objection Handling</h2>
                <p className="text-sm text-muted-foreground">{objection?.industry || 'Universal'} Industry</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} iconName="X" iconPosition="left">
              Close
            </Button>
          </div>

          {/* Objection Context */}
          <div className="bg-card rounded-xl p-6 border border-border mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={24} className="text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-foreground">Customer Objection</span>
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                    Difficulty {objection?.difficulty}/5
                  </span>
                </div>
                {/* Show the actual objection text - display_label */}
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <p className="text-base font-medium text-foreground">
                    &ldquo;{objection?.display_label || 'Objection not available'}&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* Coaching Tips - Only show tags, NOT the default_response */}
            {objection?.tags && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Lightbulb" size={16} className="text-accent" />
                  <span className="text-sm font-semibold text-foreground">Focus Areas</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {objection?.tags?.split(',')?.map((tag, index) => (
                    <span key={index} className="text-xs px-3 py-1 bg-accent/10 text-accent rounded-full">
                      {tag?.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Coaching notes if available */}
            {objection?.coaching_notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Info" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">Coaching Tip</span>
                </div>
                <p className="text-sm text-muted-foreground">{objection?.coaching_notes}</p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="text-red-500 hover:text-red-700"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Response Input */}
          {!feedback && (
            <div className="bg-card rounded-xl p-6 border border-border mb-6">
              <label className="block text-sm font-semibold text-foreground mb-3">
                Your Response
              </label>
              <textarea
                value={response}
                onChange={(e) => setResponse(e?.target?.value)}
                placeholder="Type your response to this objection... Focus on acknowledging their concern, reframing positively, and moving forward."
                rows={8}
                className="w-full p-4 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                disabled={isSubmitting}
              />
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-muted-foreground">
                  {response?.length} characters
                </span>
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  disabled={!response?.trim() || isSubmitting}
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                >
                  {isSubmitting ? 'Getting Coached by Lumo...' : 'Get Coached by Lumo'}
                </Button>
              </div>
            </div>
          )}

          {/* AI Feedback */}
          {feedback && (
            <div className="space-y-6">
              {/* Overall Score */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Lumo's Evaluation</h3>
                  <div className="text-right">
                    <div className={`text-3xl font-bold ${getScoreColor(feedback?.score)}`}>
                      {feedback?.score}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {getScoreLabel(feedback?.score)}
                    </div>
                  </div>
                </div>
                <ProgressIndicator value={feedback?.score} max={100} showPercentage={false} />
              </div>

              {/* Detailed Feedback */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="MessageSquare" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Lumo's Feedback</h3>
                </div>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-foreground whitespace-pre-line">{feedback?.feedbackText}</p>
                </div>
              </div>

              {/* Improved Response */}
              {feedback?.suggestedScript && (
                <div className="bg-card rounded-xl p-6 border border-border">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Sparkles" size={20} className="text-accent" />
                    <h3 className="text-lg font-semibold text-foreground">Lumo's Suggested Response</h3>
                  </div>
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <p className="text-sm text-foreground whitespace-pre-line">{feedback?.suggestedScript}</p>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    💡 Study this response and practice incorporating these techniques into your own approach
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="default" 
                  onClick={() => {
                    setResponse('');
                    setFeedback(null);
                    setError('');
                  }} 
                  fullWidth 
                  iconName="RotateCcw" 
                  iconPosition="left"
                >
                  Try Again
                </Button>
                <Button variant="outline" onClick={onClose} fullWidth iconName="CheckCircle" iconPosition="left">
                  Practice Another Objection
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeInterface;